import { ConciergeData } from '../types';

const API_BASE = '/api/concierge';

export type ConciergeFileType = 'image' | 'video' | 'audio';

export interface ConciergeFileItem {
  file_name: string;
  s3_key: string;
  public_url: string;
  size: number;
  last_modified: string;
}

export interface UploadConciergeFileResult {
  message: string;
  public_url: string;
  file_name: string;
  file_size: number;
  s3_key: string;
}

export class ConciergeService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  private getAuthToken() {
    return localStorage.getItem('token') ?? '';
  }

  async getConciergeData(museumCode: string): Promise<ConciergeData> {
    const response = await fetch(`${API_BASE}/${museumCode}/`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch concierge data');
    return response.json();
  }

  async updateConciergeData(museumCode: string, data: Partial<ConciergeData>): Promise<ConciergeData> {
    const response = await fetch(`${API_BASE}/${museumCode}/`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update concierge data');
    return response.json();
  }

  async updateConciergeSection<K extends keyof ConciergeData>(
    museumCode: string,
    section: K,
    data: ConciergeData[K],
  ): Promise<ConciergeData> {
    return this.updateConciergeData(museumCode, { [section]: data } as Partial<ConciergeData>);
  }

  async uploadConciergeFile(
    museumCode: string,
    file: File,
    fileType: ConciergeFileType,
    onProgress?: (progress: number) => void,
  ): Promise<UploadConciergeFileResult> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/${museumCode}/upload/`);
      xhr.setRequestHeader('Authorization', `Bearer ${this.getAuthToken()}`);

      xhr.upload.onprogress = e => {
        if (onProgress && e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status === 201) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          const body = JSON.parse(xhr.responseText);
          reject(new Error(body.error ?? 'Upload failed'));
        }
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(formData);
    });
  }

  async getConciergeFilePreviewUrl(
    museumCode: string,
    filename: string,
    fileType: ConciergeFileType = 'image',
  ): Promise<string> {
    const params = new URLSearchParams({ filename, file_type: fileType });
    const response = await fetch(`${API_BASE}/${museumCode}/preview/?${params}`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to get preview URL');
    const data = await response.json();
    return data.public_url as string;
  }

  async listConciergeFiles(
    museumCode: string,
    fileType?: ConciergeFileType,
  ): Promise<{ files: ConciergeFileItem[]; count: number }> {
    const params = fileType ? `?file_type=${fileType}` : '';
    const response = await fetch(`${API_BASE}/${museumCode}/files/${params}`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to list concierge files');
    return response.json();
  }
}

export const conciergeService = new ConciergeService();
